import { Box, Button, IconButton, Typography } from '@mui/material'
import { ChangeViewModeIcon, MinusIcon, PlusIcon } from '../Svgs'
import { BimManager } from '../../visualization/bimManager'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { createSelector } from '@reduxjs/toolkit'
import { changeViewMode } from '../../actions/bim'
import {
  BimCategoryBuildingElementTypeMapping,
  BimViewMode,
} from '../../models/Bim'
import { SceneManager } from '../../visualization/sceneManager'
import { EventType } from '../../utils/EventEmitter'
import { isBimObjectMesh } from '../../visualization/utils'
import { useEffect, useMemo, useState } from 'react'
import { updateBuildingElementInfo } from '../../services/buildingService'
import { SelectionManager } from '../../visualization/selection/selectionManager'
import BimNonEditableModalComponent from './BimNonEditableModalComponent'

const selectedBimObjectSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects.find((object) => object.id === bim.selectedBimObjectId),
)
const bimViewModeSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.viewMode,
)

const TopLeftPanel = () => {
  const selectedBimObject = useSelector(selectedBimObjectSelector)

  return (
    <Box
      sx={{
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography
        sx={{
          color: '#000000',
          fontSize: '0.84rem',
          fontWeight: 500,
        }}
      >
        {selectedBimObject?.name}
      </Typography>
      {selectedBimObject?.isNonEditable && (
        <Typography
          sx={{
            color: '#797979',
            fontSize: '0.64rem',
            fontWeight: 500,
          }}
        >
          * This BIM object is non-editable
        </Typography>
      )}
    </Box>
  )
}

const TopRightPanel = () => {
  const dispatch = useDispatch<any>()
  const bimViewMode = useSelector(bimViewModeSelector)

  const changeViewModeHandle = () => {
    const viewMode =
      bimViewMode === BimViewMode.Image
        ? BimViewMode.Model3D
        : BimViewMode.Image
    dispatch(changeViewMode(viewMode))
  }

  return (
    <Box sx={{ pointerEvents: 'auto' }}>
      <IconButton
        disableRipple
        onClick={changeViewModeHandle}
        sx={{ p: 0, height: '1rem' }}
      >
        <ChangeViewModeIcon
          sx={{
            color: '#04002E',
            fontSize: '1rem',
            '&:hover': { color: '#1705E5' },
          }}
        />
      </IconButton>
    </Box>
  )
}

const BottomLeftPanel = () => {
  const bimViewMode = useSelector(bimViewModeSelector)

  const zoomIn = () => {
    BimManager.getInstance().zoomCamera(1)
  }

  const zoomOut = () => {
    BimManager.getInstance().zoomCamera(-1)
  }

  return (
    <Box gap='0.25rem' sx={{ display: 'flex', pointerEvents: 'auto' }}>
      <IconButton
        disableRipple
        disabled={bimViewMode === BimViewMode.Image}
        size='small'
        onClick={zoomIn}
        sx={{
          background: '#CCDBED',
          borderRadius: '0.3rem',
          '&.Mui-disabled': {
            background: '#CCDBED',
          },
        }}
      >
        <PlusIcon
          sx={{
            fontSize: '1.35rem',
            color: '#04002E',
          }}
        />
      </IconButton>
      <IconButton
        disableRipple
        disabled={bimViewMode === BimViewMode.Image}
        size='small'
        onClick={zoomOut}
        sx={{
          background: '#CCDBED',
          borderRadius: '0.3rem',
          '&.Mui-disabled': {
            background: '#CCDBED',
          },
        }}
      >
        <MinusIcon
          sx={{
            fontSize: '1.35rem',
            color: '#04002E',
          }}
        />
      </IconButton>
    </Box>
  )
}

const BottomRightPanel = () => {
  const dispatch = useDispatch<any>()
  const selectedElementsIds = useSelector(
    (state: RootState) => state.construction.selectedElementsIds,
  )
  const selectedBimObjectId = useSelector(
    (state: RootState) => state.bim.selectedBimObjectId,
  )
  const selectedBimObject = useSelector(selectedBimObjectSelector)
  const selectedFile = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )

  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [isBimNonEditableDialogOpen, setIsBimNonEditableDialogOpen] =
    useState(false)

  const isDisabled = useMemo(() => {
    return !selectedElementsIds.length || isButtonDisabled
  }, [selectedElementsIds, isButtonDisabled])

  useEffect(() => {}, [selectedElementsIds])

  const proceedAddBimObject = () => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    eventEmitter?.emit(EventType.ReplaceBuildingElementOnBimObject, {
      elementsIdsToReplace: selectedElementsIds,
      bimObject: selectedBimObject,
    })

    setIsButtonDisabled(true)
    const data = selectedBimObject?.isNonEditable
      ? {
          bimobject_ref: selectedBimObjectId,
          width: selectedBimObject?.length,
          height: selectedBimObject?.height,
          thickness: selectedBimObject?.thickness,
        }
      : {
          bimobject_ref: selectedBimObjectId,
        }
    dispatch(
      updateBuildingElementInfo({
        filepath: selectedFile?.path,
        elementId: selectedElementsIds[0],
        data,
        silent: true,
      }),
    )
  }

  const addBimObjectHandle = () => {
    if (selectedBimObject?.isNonEditable) {
      setIsBimNonEditableDialogOpen(true)
      return
    }

    proceedAddBimObject()
  }

  const handleAddBimNonEditableObject = () => {
    setIsBimNonEditableDialogOpen(false)
    proceedAddBimObject()
  }

  useEffect(() => {
    const scene = SceneManager.getInstance().getScene()
    const meshes = scene?.meshes.filter(
      (m) =>
        isBimObjectMesh(m) && m.metadata.bimObjectId === selectedBimObjectId,
    )

    const selectedBimObjectMesh = meshes?.find((mesh) =>
      SelectionManager.getInstance().isSelected(mesh.id),
    )
    const selectedBimObjectMeshId = selectedBimObjectMesh?.metadata.bimObjectId

    let selectedMeshTypes: any[] = []
    if (selectedElementsIds.length) {
      const selectedMeshes = scene?.meshes.filter((m) =>
        selectedElementsIds.includes(m.id),
      )
      if (selectedMeshes) {
        selectedMeshTypes = [
          ...selectedMeshes?.map((m) =>
            isBimObjectMesh(m) ? m.metadata.sourceType : m.metadata.type,
          ),
        ]
      }
    }

    if (selectedBimObjectId) {
      setIsButtonDisabled(
        selectedBimObjectMeshId === selectedBimObjectId ||
          !selectedElementsIds.length ||
          (!!selectedBimObject &&
            !selectedMeshTypes.includes(
              BimCategoryBuildingElementTypeMapping[selectedBimObject.category],
            )),
      )
    }
  }, [selectedBimObjectId, selectedElementsIds])

  return (
    <>
      <Box sx={{ pointerEvents: 'auto' }}>
        <Button
          disableRipple
          disabled={isDisabled}
          onClick={addBimObjectHandle}
          sx={{
            background: '#1705E5',
            color: '#fff',
            fontSize: '0.84rem',
            borderRadius: '0.3rem',
            textTransform: 'none',
            padding: '0.5rem 1rem',
            '&.Mui-disabled': {
              color: '#fff',
              background: '#97999B',
            },
          }}
        >
          Add Object
        </Button>
      </Box>
      <BimNonEditableModalComponent
        isOpen={isBimNonEditableDialogOpen}
        handleClose={() => setIsBimNonEditableDialogOpen(false)}
        handleSubmit={() => handleAddBimNonEditableObject()}
      />
    </>
  )
}

interface ViewerOverlayProps {
  viewerSize: {
    width: number
    height: number
  }
}

const BimElementViewerOverlay = ({ viewerSize }: ViewerOverlayProps) => {
  const selectedBimObject = useSelector(selectedBimObjectSelector)

  if (!selectedBimObject) return null

  return (
    <Box
      sx={{
        width: viewerSize.width,
        height: viewerSize.height,
        position: 'fixed',
        pointerEvents: 'none',
        display: 'grid',
        '& > *': {
          zIndex: 100,
          display: 'flex',
          pointerEvents: 'none',
          width: viewerSize.width,
          height: viewerSize.height,
          gridArea: '1 / 1 / 1 / 1',
          padding: '1rem',
          boxSizing: 'border-box',
        },
      }}
    >
      <Box justifyContent='flex-start' alignItems='flex-start'>
        <TopLeftPanel />
      </Box>

      <Box justifyContent='flex-start' alignItems='flex-end'>
        <BottomLeftPanel />
      </Box>

      <Box justifyContent='flex-end' alignItems='flex-start'>
        <TopRightPanel />
      </Box>

      <Box justifyContent='flex-end' alignItems='flex-end'>
        <BottomRightPanel />
      </Box>
    </Box>
  )
}

export default BimElementViewerOverlay
